import {
  AppGroup,
  AppGroupParticipant,
  AppUser,
  Proposition,
  PropositionTheme,
  ReunionEvent,
  UserVote,
} from "../../utils/types";
import { db } from "../config/firebase.config";
import {
  DocumentReference,
  QueryDocumentSnapshot,
  and,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { TypeSorting } from "./typeSorting";

export const findPropositionById = async (propositionId: string) => {
  try {
    const snapshot = await getDoc(doc(db, "propositions", propositionId));

    const id = snapshot.id;
    const data = snapshot.data();

    if (!data) {
      return undefined;
    }

    const location = await getDoc(doc(db, "locations", data["location"].id));
    const theme = await getDoc(doc(db, "themes", data["theme"].id));

    const votes: UserVote[] = data["votes"] || [];
    const upvotes = votes.filter((v) => v.vote === "YES").length;
    const downvotes = votes.filter((v) => v.vote === "NO").length;

    return {
      id: id,
      title: data["title"],
      description: data["description"],
      location: location.data(),
      theme: theme.data(),
      votes: votes,
      upvotes: upvotes,
      downvotes: downvotes,
      status: data["status"],
      createdBy: data["createdBy"],
      uuid: data["uuid"],
      createdAt: data["createdAt"],
      type: data["type"],
    } as Proposition;
  } catch (error: any) {
    console.log(error);
  }
};

export const findAllPropositions = async (
  locations: string[] = [],
  themes: string[] = [],
  sorting: any = TypeSorting[1].valor
) => {
  const propositionsRef = query(
    collection(db, "propositions"),
    orderBy("createdAt", "desc")
  );

  const propositionsDocs = (await getDocs(propositionsRef)).docs;

  let propositions = await Promise.all(
    propositionsDocs.map(async (proposition) => {
      const id = proposition.id;
      const data = proposition.data();

      const location = await getDoc(doc(db, "locations", data["location"].id));
      const theme = await getDoc(doc(db, "themes", data["theme"].id));
      const votes: UserVote[] = data["votes"] || [];

      const upvotes = (
        await getDocs(
          query(
            collection(db, "user_votes"),
            and(where("proposition_id", "==", id), where("vote", "==", "YES"))
          )
        )
      ).docs.length;
      const downvotes = (
        await getDocs(
          query(
            collection(db, "user_votes"),
            and(where("proposition_id", "==", id), where("vote", "==", "NO"))
          )
        )
      ).docs.length;

      return {
        id: id,
        title: data["title"],
        description: data["description"],
        upvotes: upvotes,
        downvotes: downvotes,
        location: location.data(),
        status: data["status"],
        theme: theme.data(),
        votes: votes,
        createdBy: data["createdBy"],
        uuid: data["uuid"],
        createdAt: data["createdAt"],
        type: data["type"],
      } as Proposition;
    })
  );

  //refatorar o sort
  return propositions;
};

export const findPropositions = async (
  lastVisible: QueryDocumentSnapshot<Proposition> | "none" | null = null,
  filter: string | null = null
) => {
  let propositionsRef = query(collection(db, "propositions"));

  if (filter) {
    propositionsRef = query(propositionsRef, where("status", "==", filter));
  }

  if (lastVisible) {
    propositionsRef = query(
      propositionsRef,
      orderBy("createdAt", "desc"),
      startAfter(lastVisible),
      limit(30)
    );
  } else {
    propositionsRef = query(
      propositionsRef,
      orderBy("createdAt", "desc"),
      limit(30)
    );
  }

  const propositionsDocs = (await getDocs(propositionsRef)).docs;

  let propositions = await Promise.all(
    propositionsDocs.map(async (proposition) => {
      const id = proposition.id;
      const data = proposition.data();

      const location = await getDoc(doc(db, "locations", data["location"].id));
      const theme = await getDoc(doc(db, "themes", data["theme"].id));
      const votes: UserVote[] = data["votes"] || [];

      const upvotes = (
        await getDocs(
          query(
            collection(db, "user_votes"),
            and(where("proposition_id", "==", id), where("vote", "==", "YES"))
          )
        )
      ).docs.length;
      const downvotes = (
        await getDocs(
          query(
            collection(db, "user_votes"),
            and(where("proposition_id", "==", id), where("vote", "==", "NO"))
          )
        )
      ).docs.length;

      return {
        id: id,
        title: data["title"],
        description: data["description"],
        upvotes: upvotes,
        downvotes: downvotes,
        location: location.data(),
        status: data["status"],
        theme: theme.data(),
        votes: votes,
        createdBy: data["createdBy"],
        uuid: data["uuid"],
        createdAt: data["createdAt"],
        type: data["type"],
      } as Proposition;
    })
  );

  const updatedLastVisible = propositionsDocs[
    propositionsDocs.length - 1
  ] as QueryDocumentSnapshot<Proposition>;

  //refatorar o sort
  return {
    propositions,
    updatedLastVisible,
  };
};

export const findUserTokenByUserEmail = async (userEmail: string) => {
  try {
    const snapshot = await getDoc(doc(db, "user_tokens", userEmail));

    const data = snapshot.data();

    if (!data) {
      return undefined;
    }

    return {
      userEmail: data["userEmail"],
      token: data["token"],
    };
  } catch (error: any) {
    console.log(error);
  }
};

export const findPropositionByUuid = async (uuid: string) => {
  const q = query(collection(db, "propositions"), where("uuid", "==", uuid));

  const docs = (await getDocs(q)).docs;

  if (docs.length === 1) {
    const document = docs[0];
    const id = document.id;
    const data = document.data();

    const location = await getDoc(doc(db, "locations", data["location"].id));
    const theme = await getDoc(doc(db, "themes", data["theme"].id));

    const votes: UserVote[] = data["votes"] || [];

    const upvotes = votes.filter((v) => v.vote === "YES").length;
    const downvotes = votes.filter((v) => v.vote === "NO").length;

    return {
      id: id,
      title: data["title"],
      uuid: data["uuid"],
      votes: votes,
      description: data["description"],
      location: location.data(),
      theme: theme.data(),
      upvotes: upvotes,
      downvotes: downvotes,
      status: data["status"],
      createdAt: data["createdAt"],
      createdBy: data["createdBy"],
      type: data["type"],
    } as Proposition;
  }
};

export const findUserById = async (userId: string) => {
  const snapshot = await getDoc(doc(db, "users", userId));

  if (snapshot.exists()) {
    const data = snapshot.data();

    return {
      name: data["name"],
      birthday: data["birthday"],
      email: data["email"],
      cpf: data["cpf"],
    } as AppUser;
  }

  return null;
};

export const findAllThemes = async (): Promise<PropositionTheme[]> => {
  const themesRef = query(collection(db, "themes"), orderBy("name"));
  const themesDocs = (await getDocs(themesRef)).docs;

  return themesDocs.map((document) => {
    const data = document.data();
    return {
      id: document.id,
      name: data["name"],
      color: data["color"],
      imageUrl: data["imageUrl"],
    } as PropositionTheme;
  });
};

export const findIsAllowedUserByEmail = async (email: string) => {
  try {
    const lowerEmail = email.toLowerCase();
    console.log("Procurando usuário permitido:", lowerEmail);
    
    const q = query(
      collection(db, "allowed_users"),
      where("email", "==", lowerEmail)
    );

    const docs = (await getDocs(q)).docs;

    if (docs.length < 1) {
      console.warn(`Nenhum usuário encontrado com email: ${lowerEmail}`);
      return undefined;
    }

    const data = docs[0].data();
    const foundEmail = data["email"] as string;
    console.log(`Usuário permitido encontrado: ${foundEmail}`);
    return foundEmail;
  } catch (error) {
    console.error("Erro ao buscar usuário permitido:", error);
    throw error;
  }
};

export const findGroups = async (): Promise<AppGroup[]> => {
  try {
    const groupsDocs = (await getDocs(collection(db, "groups"))).docs;

    return await Promise.all(
      groupsDocs.map(async (groupDoc) => {
        const data = groupDoc.data();

        const participants: AppGroupParticipant[] = Array.isArray(data["participants"])
          ? data["participants"].map((participant: any) => ({
              name: participant?.name || "Sem nome",
              phone: participant?.phone || "Sem telefone",
              isResponsible: Boolean(participant?.isResponsible),
            }))
          : [];

        const locationRef = data["location"] as DocumentReference | undefined;
        const themeRef = data["theme"] as DocumentReference | undefined;

        const [locationSnapshot, themeSnapshot] = await Promise.all([
          locationRef ? getDoc(locationRef) : Promise.resolve(null),
          themeRef ? getDoc(themeRef) : Promise.resolve(null),
        ]);

        const locationName =
          locationSnapshot?.data()?.["name"] ||
          data["meetingLocation"]?.["neighborhood"] ||
          data["meetingLocation"]?.["address"] ||
          "Nao informado";

        const themeName =
          themeSnapshot?.data()?.["name"] || data["theme"]?.id || "Nao informado";

        return {
          id: groupDoc.id,
          createdBy: data["createdBy"] || "",
          location: locationName,
          theme: themeName,
          description: data["description"] || "",
          meetingLocation: {
            address: data["meetingLocation"]?.["address"] || "",
            number: data["meetingLocation"]?.["number"] || "",
            neighborhood: data["meetingLocation"]?.["neighborhood"] || "",
            complement: data["meetingLocation"]?.["complement"] || "",
          },
          participants,
        } as AppGroup;
      })
    );
  } catch (error) {
    console.error("Erro ao consultar grupos:", error);
    return [];
  }
};

export const findEvents = async (): Promise<ReunionEvent[]> => {
  try {
    const eventsDocs = (await getDocs(collection(db, "events"))).docs;
    const events = eventsDocs.map((eventDoc) => {
      const data = eventDoc.data();
      return {
        id: eventDoc.id,
        title: data["title"] || "",
        url: data["url"] || "",
        imageUrl: data["imageUrl"] || "",
        description: data["description"] || "",
        date: data["date"] || "",
        time: data["time"] || "",
        location: data["location"] || "",
      } as ReunionEvent;
    });
    return events;
  } catch (error) {
    console.error("Erro ao consultar eventos:", error);
    return [];
  }
};
